import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Alert, StatefulButton } from '@edx/paragon';
import { EnvelopeIcon } from "@phosphor-icons/react";
import { FormattedMessage } from '@edx/frontend-platform/i18n';

import PropTypes from 'prop-types';
import messages from '../messages';

import { PasswordField } from '../../common-components';
import ArrowButton from '../../common-components/ArrowButton';
import PasswordStrength from '../../common-components/PasswordStrength';
import FieldRenderer from '../../field-renderer/FieldRenderer';
import TermsOfService from '../../terms-of-service/TermsOfService';

import { COMPLETE_STATE, REGISTER_PAGE } from '../../data/constants';
import { isHostAvailableInQueryParams } from '../../data/utils';
import { validateConfirmPasswordField, passwordConditions } from '../../data/utils/passwordValidator';
import { registerNewUser, setEmailSuggestionInStore, fetchRealtimeValidations } from '../data/actions';
import { FIELDS, FORM_SUBMISSION_ERROR } from '../data/constants';
import { getBackendValidations, prepareRegistrationPayload, validateRegistrationForm, getProviderObjLabel} from '../data/utils';
import { EmailField, NameField } from '../RegistrationFields';


const ManualAccountCreation = ({
  providers,
  referrer,
  onSelectPage,
  formFields,
  setErrorCode,
  handleOnChange,
  handleGoToNextStep,
  handleGoToPrevStep,
  queryParams,
  formStartTime,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [errors, setErrors] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    username: '',
    terms_of_service: '',
  });

  const registrationEmbedded = isHostAvailableInQueryParams();
  const [showTerms, setShowTerms] = useState(false);

  const {
    userPipelineDataLoaded,
    usernameSuggestions,
    submitState,
    validations,
    registrationError,
  } = useSelector((state) => state.register);

  const {
    thirdPartyAuthContext: { autoSubmitRegForm, currentProvider },
  } = useSelector((state) => state.commonComponents);

  const backendValidations = useMemo(
    () => getBackendValidations(registrationError, validations),
    [registrationError, validations]
  );

  const { validationApiRateLimited } = useSelector((state) => state.register);

  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  const flags = {
    showConfigurableEdxFields: getConfig().SHOW_CONFIGURABLE_EDX_FIELDS,
    showConfigurableRegistrationFields:
      getConfig().ENABLE_DYNAMIC_REGISTRATION_FIELDS,
    showMarketingEmailOptInCheckbox: getConfig().MARKETING_EMAILS_OPT_IN,
  };

  // initializing the current provider object after redirect from the idp provider
  const providerObj = currentProvider
    ? providers.find(
        (provider) =>
          provider.id === currentProvider || provider.name === currentProvider
      )
    : null;

  const hasProvider = Boolean(currentProvider);

  // building icon source for the provider
  let iconSrc = null;
  if (providerObj?.iconImage) {
    const { iconImage } = providerObj;
    iconSrc = iconImage.startsWith('http')
      ? iconImage
      : getConfig().LMS_BASE_URL + iconImage;
  }
  // biulding provider label for translation
  const providerLabel = providerObj
    ? getProviderObjLabel(providerObj, messages, formatMessage)
    : '';

  const validatePasswordConditions = (value) => {
    const validation = passwordConditions(value);
    const isValid = Object.values(validation).every(Boolean);
    setShowTooltip(!isValid);
    return isValid;
  };

  const validateConfirmPassword = (value, newPassword) => {
    const error = validateConfirmPasswordField(value, newPassword, formatMessage);
    setErrors((prev) => ({ ...prev, confirm_password: error }));
  };

  const handlePasswordChange = (e) => {
    handleOnChange(e);
    validatePasswordConditions(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;
    handleOnChange(e);
    validateConfirmPassword(value, formFields.password);
  };

  const splitName = (full = '') => {
    const fullNameWords = full.trim().split(/\s+/).filter(Boolean);
    return {
      first_name: fullNameWords[0] || '',
      // Assumption: any middle name(s) will be included in last_name
      last_name: fullNameWords.slice(1).join(' '), 
    };
  };

  const handleErrorChange = (fieldName, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: error,
    }));
  };

  /**
   * Check if there are any validation errors to disable the submit button.
   * Excludes 'errorCode' fields because they are metadata used for conditional logic
   * (use for showing different Alert messages, and not actual error messages to display.)
   */
  const hasValidationErrors = () => {
    return Object.entries(errors)
      .filter(([key]) => !key.includes('errorCode'))
      .some(([, value]) => value);
  };

  const handleFinalSubmit = () => {
    const totalRegistrationTime = (Date.now() - formStartTime) / 1000;
    const { first_name, last_name } = splitName(formFields.full_name || '');

    let payload = {
      ...formFields,
      first_name,
      last_name,
      name: (formFields.full_name).trim() ,
      username: usernameSuggestions[0],
    };

    if (currentProvider) {
      delete payload.password;
      delete payload.confirm_password;
      payload.social_auth_provider = currentProvider;
    }

    // Validating form data before submitting
    const {
      isValid: areEmailAndPasswordValid,
      fieldErrors: emailAndPasswordErrors,
      emailSuggestion,
    } = validateRegistrationForm(payload, errors, formatMessage);

    // check each field and update the
    setErrors((prev) => ({ ...prev, ...emailAndPasswordErrors }));
    dispatch(setEmailSuggestionInStore(emailSuggestion));

    // returning if not valid
    if (!areEmailAndPasswordValid) {
      setErrorCode((prevState) => ({
        type: FORM_SUBMISSION_ERROR,
        count: prevState.count + 1,
      }));
      return;
    }

    // Preparing payload for submission
    payload = prepareRegistrationPayload(
      payload,
      flags.showMarketingEmailOptInCheckbox,
      totalRegistrationTime,
      queryParams
    );

    // making register call
    dispatch(registerNewUser(payload));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!formFields.full_name) {
        return;
      }

      if (!validationApiRateLimited) {
        dispatch(fetchRealtimeValidations({ name: (formFields.full_name).trim() }));
      }
    }, 1000);

    return () => clearTimeout(handler);
  }, [
    formFields.full_name,
    validationApiRateLimited,
    dispatch,
  ]);

  useEffect(() => {
    if (autoSubmitRegForm && userPipelineDataLoaded) {
      handleFinalSubmit();
    }
  }, [autoSubmitRegForm, userPipelineDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (backendValidations) {
      if (!registrationEmbedded) {
        setErrors((prevErrors) => ({ ...prevErrors, ...backendValidations }));
      }
    }
  }, [backendValidations, registrationEmbedded]);

  useEffect(() => {
    // For SSO registrations, skip handleGoToNextStep - let the redirect handle step transition
    // For regular registrations, proceed to next step normally
    if (submitState === COMPLETE_STATE && !currentProvider) {
      handleGoToNextStep();
    }
  }, [submitState, handleGoToNextStep, currentProvider]);


  return (
    <>
      <div className="flex-center-column flex-fill manual-account-creation">
        <div className="flex-center-column content-wrapper w-100">
          {!hasProvider && (
            <>
              <h1 className="text-black form-header">
                {formatMessage(
                  messages['intro.page.create.account.header'],
                  {
                    enter: <br />,
                  }
                )}
              </h1>
              <p className="subtitle regular-text text-center">
                {formatMessage(
                  messages['manual.account.creation.page.subtitle']
                )}
              </p>
            </>
          )}
          <div className="flex-center-column name-fields w-100">
            <div className="sso-account-creation-validation" 
              data-gtm={
                providerObj
                ? `sso_registration_attempt_${providerObj.name.toLowerCase()}`
                : 'manual_registration_attempt'
              }
            >
              {referrer === REGISTER_PAGE && providerObj && (
                <div className="flex-start-column">
                  <h1 className="form-title mb-1">
                    {formatMessage(
                      messages['sso.account.creation.account.validation.title']
                    )}
                  </h1>
                  <p className="subtitle regular-text mb-4">
                    <FormattedMessage
                      {...messages[
                        'sso.account.creation.account.validation.subtitle'
                      ]}
                      values={{
                        lineBreak: <br />,
                      }}
                    />
                  </p>
                  <div className='mb-3'> 
                    <div className="d-flex align-items-center">
                      <img
                        src={iconSrc}
                        alt={`${providerObj.name} icon`}
                        width={20}
                        height={20}
                      />
                      <p className="provider-name mb-0 ml-2">
                        <FormattedMessage
                          {...messages[
                            'sso.account.creation.account.connected.with'
                          ]}
                          values={{
                            providerName: (
                              <span className="font-weight-bold">
                                {providerLabel}
                              </span>
                            ),
                          }}
                        />
                      </p>
                    </div>
                    <div className="d-flex align-items-center"> 
                      <EnvelopeIcon size={22}/>
                      <p className='ml-2 mb-0'>
                        {formFields.email}
                      </p>
                    </div>
                  </div>
                  {errors.email && (
                    <Alert variant="danger" className="mt-3">
                      {registrationError?.errorCode === 'duplicate-email' ? (
                        <FormattedMessage
                          {...messages['sso.account.creation.duplicate.email.error']}
                          values={{
                            loginLink: (
                              <a
                                href={`${getConfig().AUTHN_MICROFRONTEND_URL || window.location.origin}/authn/login`}
                                className="alert-link font-weight-bold"
                              >
                                {formatMessage(messages['sso.account.creation.duplicate.email.login.link'])}
                              </a>
                            ),
                          }}
                        />
                      ) : (
                        errors.email
                      )}
                    </Alert>
                  )}
                </div>
              )}
            </div>
            <NameField
              name="full_name"
              fieldName="full_name"
              value={formFields.full_name}
              className="logistration-input"
              handleChange={handleOnChange}
              handleErrorChange={handleErrorChange}
              errorMessage={errors.full_name}
              showErrorMessage
              floatingLabel={formatMessage(
                messages['registration.full_name.input.placeholder']
              )}
              placeholder={formatMessage(
                messages['registration.full_name.placeholder']
              )}
            />
            {!hasProvider && (
              <EmailField
                name="email"
                value={formFields.email}
                className="logistration-input"
                handleErrorChange={handleErrorChange}
                handleChange={handleOnChange}
                errorMessage={errors.email}
                showErrorMessage
                absoluteLabel={formatMessage(
                  messages['logistration.email.input.label']
                )}
                placeholder={formatMessage(
                  messages['logistration.email.input.placeholder']
                )}
              />
            )}
            {!currentProvider && (
                <div className="password-input position-relative">
                  <PasswordField
                    name="password"
                    showTooltip={showTooltip}
                    value={formFields.password}
                    className="logistration-input"
                    handleChange={handlePasswordChange}
                    errorMessage={errors.password}
                    showErrorMessage
                    borderClass="bold-text"
                    showStatusIcon={Boolean(
                      formFields.password && formFields.confirm_password
                    )}
                    passwordStatusSuccess={
                      formFields.password === formFields.confirm_password
                    }
                    handleErrorChange={handleErrorChange}
                    absoluteLabel={formatMessage(
                      messages['password.input.choose.password.label']
                    )}
                    placeholder={formatMessage(
                      messages['password.input.choose.password.placeholder']
                    )}
                  />
                  <PasswordStrength 
                    value={formFields.password} 
                    confirmPasswordValue={formFields.confirm_password}
                  />
                  <PasswordField
                    name="confirm_password"
                    value={formFields.confirm_password}
                    className="logistration-input"
                    handleChange={handleConfirmPasswordChange}
                    errorMessage={errors.confirm_password}
                    showErrorMessage
                    borderClass="bold-text"
                    mainPasswordValue={formFields.password}
                    showStatusIcon={Boolean(
                      formFields.password && formFields.confirm_password
                    )}
                    passwordStatusSuccess={
                      formFields.password === formFields.confirm_password
                    }
                    handleErrorChange={handleErrorChange}
                    absoluteLabel={formatMessage(
                      messages['password.input.confirm.password.label']
                    )}
                    placeholder={formatMessage(
                      messages['password.input.confirm.password.placeholder']
                    )}
                  />
                </div>
            )}
            <div className="flex-start-column gap-25 w-100 manual-btn-group">
              <div className="d-flex flex-column gap-20 checkboxes">
                <FieldRenderer
                  className="cursor-pointer"
                  labelClassName="text-black"
                  errorMessage={errors.terms_of_service}
                  fieldData={{
                    type: 'checkbox',
                    label: formatMessage(
                      messages['terms.of.service.checkbox.label'],
                      {
                        platform_name: getConfig().SITE_NAME,
                        termsLink: (
                          <button
                            type="button"
                            className={`link-style text-black p-0 ${ errors.terms_of_service && 'invalid-red-color'}`}
                            onClick={() => setShowTerms(true)}
                          >
                            {formatMessage(messages['terms.of.service.text'])}
                          </button>
                        ),
                      }
                    ),
                    name: FIELDS.TERMS_OF_SERVICE,
                  }}
                  value={formFields[FIELDS.TERMS_OF_SERVICE]}
                  handleFocus={() =>
                    handleErrorChange(FIELDS.TERMS_OF_SERVICE, '')
                  }
                  onChangeHandler={handleOnChange}
                />
                {showTerms && (
                  <TermsOfService handleClose={() => setShowTerms(false)} />
                )}
              </div>
              <div className="page-button-forget">
                <StatefulButton
                  id="next-step"
                  name="next-step"
                  type="button"
                  className="logistration-btn"
                  state={submitState}
                  labels={{
                    default: formatMessage( messages['continue.button']), 
                    pending: '',
                  }}
                  onClick={handleFinalSubmit}
                  disabled={hasValidationErrors()}
                  style={{ opacity: hasValidationErrors() ? 0.5 : 1 }}
                />
                {!currentProvider && (
                  <ArrowButton className="page-button-forget-single-back" onClick={handleGoToPrevStep} />
                )}
              </div>
              {!currentProvider && (
                <p className="regular-text">
                  {formatMessage(
                    messages['intro.page.already.registered.text']
                  )}
                  <button
                    className="link-style"
                    type="button"
                    onClick={onSelectPage}
                  >
                    {formatMessage(messages['intro.page.sign.in.here.link'])}
                  </button>
                </p>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ManualAccountCreation.propTypes = {
  formFields: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    password: PropTypes.string,
    confirmPassword: PropTypes.string,
  }).isRequired,
  setErrorCode: PropTypes.func.isRequired,
  errorCode: PropTypes.shape({}).isRequired,
  handleOnChange: PropTypes.func,
  handleGoToNextStep: PropTypes.func.isRequired,
  handleGoToPrevStep: PropTypes.func.isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  formStartTime: PropTypes.number.isRequired,
};

ManualAccountCreation.defaultProps = {
  handleOnChange: () => { },
};

export default ManualAccountCreation;
